import { useMemo, useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import {
  App as AntdApp,
  Button,
  Card,
  Drawer,
  Form,
  Input,
  InputNumber,
  Popconfirm,
  Segmented,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import {
  DeleteOutlined,
  EditOutlined,
  LinkOutlined,
  PlusOutlined,
  ReloadOutlined,
} from '@ant-design/icons'
import { supabase } from '@/lib/supabase'
import { revalidateNextTag } from '@/lib/revalidate'
import { formatCST } from '@/lib/time'
import { AdminMobileList } from '@/components/AdminMobileList'
import { AdminRecordActions } from '@/components/AdminRecordActions'
import { useIsMobile } from '@/lib/hooks'

export const Route = createFileRoute('/_authed/friend-links')({
  component: FriendLinksPage,
})

type ActiveFilter = 'active' | 'inactive' | 'all'

interface FriendLinkRow {
  id: string
  name: string
  url: string
  description: string | null
  sort_order: number
  is_active: boolean
  open_in_new_tab: boolean
  created_by: string | null
  created_at: string
  updated_at: string
}

interface FriendLinkFormValues {
  name: string
  url: string
  description?: string
  sort_order: number
  is_active: boolean
  open_in_new_tab: boolean
}

function FriendLinksPage() {
  const qc = useQueryClient()
  const { message } = AntdApp.useApp()
  const isMobile = useIsMobile()

  const [activeFilter, setActiveFilter] = useState<ActiveFilter>('all')
  const [editing, setEditing] = useState<FriendLinkRow | 'new' | null>(null)
  const [form] = Form.useForm<FriendLinkFormValues>()

  const listQuery = useQuery({
    queryKey: ['friend-links', activeFilter],
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .schema('admin')
        .rpc('list_friend_links', {
          p_active: activeFilter,
          p_limit: 200,
          p_offset: 0,
        })
      if (error) throw error
      const r = data as {
        ok: boolean
        error?: string
        rows?: FriendLinkRow[]
        total?: number
      }
      if (!r.ok) throw new Error(r.error ?? 'unknown')
      return r.rows ?? []
    },
  })

  const saveMutation = useMutation({
    mutationFn: async (values: FriendLinkFormValues) => {
      const { data, error } = await (supabase as any)
        .schema('admin')
        .rpc('upsert_friend_link', {
          p_id: editing === 'new' ? null : (editing?.id ?? null),
          p_name: values.name,
          p_url: values.url,
          p_description: values.description?.trim() || null,
          p_sort_order: values.sort_order,
          p_is_active: values.is_active,
          p_open_in_new_tab: values.open_in_new_tab,
          p_reason: null,
        })
      if (error) throw error
      const r = data as { ok: boolean; error?: string }
      if (!r.ok) throw new Error(r.error ?? 'unknown')
    },
    onSuccess: async () => {
      message.success(editing === 'new' ? '友链已创建' : '友链已更新')
      setEditing(null)
      await qc.invalidateQueries({ queryKey: ['friend-links'] })
      void revalidateNextTag('friend-links')
    },
    onError: (e: Error) => message.error(`保存失败：${e.message}`),
  })

  const toggleMutation = useMutation({
    mutationFn: async ({ id, isActive }: { id: string; isActive: boolean }) => {
      const { data, error } = await (supabase as any)
        .schema('admin')
        .rpc('set_friend_link_active', {
          p_id: id,
          p_is_active: isActive,
          p_reason: null,
        })
      if (error) throw error
      const r = data as { ok: boolean; error?: string }
      if (!r.ok) throw new Error(r.error ?? 'unknown')
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['friend-links'] })
      void revalidateNextTag('friend-links')
    },
    onError: (e: Error) => message.error(`操作失败：${e.message}`),
  })

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { data, error } = await (supabase as any)
        .schema('admin')
        .rpc('delete_friend_link', { p_id: id, p_reason: null })
      if (error) throw error
      const r = data as { ok: boolean; error?: string }
      if (!r.ok) throw new Error(r.error ?? 'unknown')
    },
    onSuccess: async () => {
      message.success('友链已删除')
      await qc.invalidateQueries({ queryKey: ['friend-links'] })
      void revalidateNextTag('friend-links')
    },
    onError: (e: Error) => message.error(`删除失败：${e.message}`),
  })

  const rows = listQuery.data ?? []

  const openEdit = (row: FriendLinkRow) => {
    setEditing(row)
    form.setFieldsValue({
      name: row.name,
      url: row.url,
      description: row.description ?? undefined,
      sort_order: row.sort_order,
      is_active: row.is_active,
      open_in_new_tab: row.open_in_new_tab,
    })
  }

  const columns: ColumnsType<FriendLinkRow> = useMemo(
    () => [
      {
        title: '名称',
        dataIndex: 'name',
        key: 'name',
        render: (name: string, row) => (
          <Space direction="vertical" size={0}>
            <Typography.Text strong>{name}</Typography.Text>
            {row.description ? (
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {row.description}
              </Typography.Text>
            ) : null}
          </Space>
        ),
      },
      {
        title: '链接',
        dataIndex: 'url',
        key: 'url',
        render: (url: string) => (
          <Typography.Link href={url} target="_blank" rel="noopener noreferrer">
            {url}
          </Typography.Link>
        ),
      },
      {
        title: '排序',
        dataIndex: 'sort_order',
        key: 'sort_order',
        width: 72,
      },
      {
        title: '状态',
        key: 'status',
        width: 96,
        render: (_, row) => (
          <Tag color={row.is_active ? 'green' : 'default'}>
            {row.is_active ? '上线' : '下线'}
          </Tag>
        ),
      },
      {
        title: '新窗口',
        dataIndex: 'open_in_new_tab',
        key: 'open_in_new_tab',
        width: 88,
        render: (v: boolean) => (v ? '是' : '否'),
      },
      {
        title: '更新时间',
        dataIndex: 'updated_at',
        key: 'updated_at',
        width: 168,
        render: (v: string) => formatCST(v),
      },
      {
        title: '操作',
        key: 'actions',
        width: 168,
        fixed: 'right',
        render: (_, row) => (
          <Space size={4}>
            <Tooltip title="编辑">
              <Button
                type="text"
                icon={<EditOutlined />}
                onClick={() => {
                  setEditing(row)
                  form.setFieldsValue({
                    name: row.name,
                    url: row.url,
                    description: row.description ?? undefined,
                    sort_order: row.sort_order,
                    is_active: row.is_active,
                    open_in_new_tab: row.open_in_new_tab,
                  })
                }}
              />
            </Tooltip>
            <Switch
              size="small"
              checked={row.is_active}
              loading={toggleMutation.isPending}
              onChange={(checked) =>
                toggleMutation.mutate({ id: row.id, isActive: checked })
              }
            />
            <Popconfirm
              title="确定删除这条友链？"
              okText="删除"
              okButtonProps={{ danger: true }}
              cancelText="取消"
              onConfirm={() => deleteMutation.mutate(row.id)}
            >
              <Button type="text" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          </Space>
        ),
      },
    ],
    [deleteMutation, form, toggleMutation.isPending],
  )

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <Card>
        <Space
          align="center"
          style={{ width: '100%', justifyContent: 'space-between' }}
          wrap
        >
          <Space direction="vertical" size={4}>
            <Typography.Title level={4} style={{ margin: 0 }}>
              友情链接
            </Typography.Title>
            <Typography.Paragraph type="secondary" style={{ margin: 0 }}>
              管理 Web 页脚「友情链接」区块。保存后会自动刷新 C 端缓存。
            </Typography.Paragraph>
          </Space>
          <Space wrap>
            <Segmented
              value={activeFilter}
              options={[
                { value: 'all', label: '全部' },
                { value: 'active', label: '上线' },
                { value: 'inactive', label: '下线' },
              ]}
              onChange={(v) => setActiveFilter(v as ActiveFilter)}
            />
            <Tooltip title={isMobile ? '刷新' : undefined}>
              <Button
                icon={<ReloadOutlined />}
                aria-label="刷新"
                onClick={() => listQuery.refetch()}
                loading={listQuery.isFetching}
              >
                {isMobile ? null : '刷新'}
              </Button>
            </Tooltip>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setEditing('new')
                form.resetFields()
                form.setFieldsValue({
                  sort_order: 0,
                  is_active: true,
                  open_in_new_tab: true,
                })
              }}
            >
              新建友链
            </Button>
          </Space>
        </Space>
      </Card>

      <Card>
        <div className="admin-table-wrap">
          <div className="admin-desktop-table">
            <Table
              rowKey="id"
              columns={columns}
              dataSource={rows}
              loading={listQuery.isLoading}
              pagination={false}
              scroll={{ x: 960 }}
            />
          </div>
        </div>
        {/* 手机：卡片列表（点开即编辑；上下线开关 + 删除放在卡片底部） */}
        <AdminMobileList<FriendLinkRow>
          rows={rows}
          rowKey={(row) => row.id}
          loading={listQuery.isLoading || listQuery.isFetching}
          renderItem={(row) => (
            <article
              className="admin-mobile-record"
              role="button"
              tabIndex={0}
              onClick={() => openEdit(row)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') openEdit(row)
              }}
            >
              <div className="admin-mobile-record__head">
                <div className="admin-mobile-record__title">{row.name}</div>
                <Tag color={row.is_active ? 'green' : 'default'}>
                  {row.is_active ? '上线' : '下线'}
                </Tag>
              </div>
              <div className="admin-mobile-record__body friend-links-mobile-url">
                <Typography.Link
                  href={row.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                >
                  {row.url}
                </Typography.Link>
              </div>
              <div className="admin-mobile-record__meta">
                {row.description ? <span>{row.description}</span> : null}
                <span>排序 {row.sort_order}</span>
                <span>{row.open_in_new_tab ? '新窗口' : '当前窗口'}</span>
                <span>更新 {formatCST(row.updated_at, 'MM-DD HH:mm')}</span>
              </div>
              <AdminRecordActions
                leading={
                  <>
                    <Switch
                      size="small"
                      checked={row.is_active}
                      aria-label="上线"
                      loading={
                        toggleMutation.isPending &&
                        toggleMutation.variables?.id === row.id
                      }
                      onChange={(checked) =>
                        toggleMutation.mutate({ id: row.id, isActive: checked })
                      }
                    />
                    上线
                  </>
                }
                actions={[
                  {
                    key: 'delete',
                    label: '删除',
                    icon: <DeleteOutlined />,
                    danger: true,
                    confirm: { title: '确定删除这条友链？', okText: '删除' },
                    onClick: () => deleteMutation.mutate(row.id),
                  },
                ]}
              />
            </article>
          )}
        />
      </Card>

      <Drawer
        title={editing === 'new' ? '新建友链' : '编辑友链'}
        open={editing !== null}
        width={isMobile ? '100%' : 480}
        destroyOnClose
        onClose={() => setEditing(null)}
        footer={
          <Space style={{ justifyContent: 'flex-end', width: '100%' }}>
            <Button onClick={() => setEditing(null)}>取消</Button>
            <Button
              type="primary"
              loading={saveMutation.isPending}
              onClick={() => form.submit()}
            >
              保存
            </Button>
          </Space>
        }
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={(values) => saveMutation.mutate(values)}
        >
          <Form.Item
            name="name"
            label="名称"
            rules={[{ required: true, message: '请输入友链名称' }]}
          >
            <Input
              placeholder="例如：Bangumi 番组计划"
              maxLength={64}
              showCount
            />
          </Form.Item>
          <Form.Item
            name="url"
            label="链接"
            rules={[
              { required: true, message: '请输入链接' },
              {
                pattern: /^https:\/\/\S+$/,
                message: '必须是 https:// 开头的合法 URL',
              },
            ]}
          >
            <Input prefix={<LinkOutlined />} placeholder="https://..." />
          </Form.Item>
          <Form.Item name="description" label="备注（可选）">
            <Input.TextArea
              rows={2}
              placeholder="页脚 hover 提示，可不填"
              maxLength={120}
              showCount
            />
          </Form.Item>
          <Form.Item
            name="sort_order"
            label="排序"
            tooltip="数字越小越靠前；同值按最近更新在前"
          >
            <InputNumber min={0} max={9999} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="is_active" label="上线" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item
            name="open_in_new_tab"
            label="新窗口打开"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
        </Form>
      </Drawer>
    </Space>
  )
}
